import { UxType } from '@logz-pkg/enums';
import { LoggerService } from '../core/logger/logger.service';

// Contract shared with osd-2-0/dashboards/src/legacy/ui/ui_render/bootstrap/template.js.hbs
// failure() — keep the string value in sync when changing.
export const LOGZIO_OSD_BOOTSTRAP_FAILURE_MESSAGE_TYPE = 'logzio:osd-bootstrap-failure';

export interface IOsdBootstrapFailureMessage {
  type: typeof LOGZIO_OSD_BOOTSTRAP_FAILURE_MESSAGE_TYPE;
  failedUrl?: string;
}

/** postMessage payload emitted by OSD bootstrap failure(). */
export type OsdBootstrapFailureMessageEvent = MessageEvent<IOsdBootstrapFailureMessage>;

/**
 * Product iframe contentWindow — the only MessageEventSource we handle for OSD bootstrap
 * failures. MessageEventSource cannot be generic in the DOM lib, so we narrow to Window and
 * treat frameElement as the embedding kibana iframe.
 */
export interface IOsdProductIframeWindow extends Window {
  readonly frameElement: HTMLIFrameElement | null;
}

// OSD's bootstrap attaches an 'error' listener to every <script>/<link> it appends. On the
// first asset failure it notifies the parent via postMessage and replaces document.body with
// a red error banner. Most reports are transient (deploy-rollout version skew, one-off 5xx on
// a bundle) and self-heal on refresh.
//
// Parent registers a window 'message' listener before the iframe loads. On bootstrap failure
// the iframe posts { type, failedUrl } and the parent reloads the product iframe once per
// retry window with a cache-busting query param (there is no programmatic hard-reload API).

const RETRY_STATE_KEY = 'logz_osd_bootstrap_retry_state';
const MAX_RETRIES = 1;
const RETRY_RESET_MS = 60_000;
const SUCCESS_WINDOW_MS = 10_000;

interface IOsdBootstrapRetryState {
  count: number;
  at: number;
}

let successCheckTimeout: ReturnType<typeof setTimeout> | null = null;

const readRetryState = (): IOsdBootstrapRetryState | null => {
  try {
    const raw = sessionStorage.getItem(RETRY_STATE_KEY);

    if (!raw) return null;

    const state = JSON.parse(raw) as IOsdBootstrapRetryState;

    if (typeof state.count !== 'number' || typeof state.at !== 'number') return null;

    if (Date.now() - state.at >= RETRY_RESET_MS) return null;

    return state;
  } catch {
    return null;
  }
};

const readRetries = (): number => readRetryState()?.count ?? 0;

const writeRetries = (count: number): boolean => {
  try {
    const stateToWrite: IOsdBootstrapRetryState = { count, at: Date.now() };

    sessionStorage.setItem(RETRY_STATE_KEY, JSON.stringify(stateToWrite));

    return true;
  } catch {
    return false;
  }
};

const clearRetries = (): void => {
  try {
    sessionStorage.removeItem(RETRY_STATE_KEY);
  } catch {
    // sessionStorage may be unavailable (e.g. privacy modes) — nothing to clear.
  }
};

const cancelOsdBootstrapRecoverySuccessSchedule = (): void => {
  if (!successCheckTimeout) return;

  clearTimeout(successCheckTimeout);
  successCheckTimeout = null;
};

/** Bust HTTP cache by navigating to a unique URL. Not read by app routing logic. */
const reloadBypassingCache = (targetWindow: Window): void => {
  const url = new URL(targetWindow.location.href);

  url.searchParams.set('_cb', String(Date.now()));
  targetWindow.location.replace(url.toString());
};

const tryRecoverFromOsdBootstrapFailure = (productWindow: IOsdProductIframeWindow): void => {
  const retries = readRetries();

  if (retries >= MAX_RETRIES) return;

  if (!writeRetries(retries + 1)) return;

  reloadBypassingCache(productWindow);
};

const isOsdBootstrapFailurePayload = (data: unknown): data is IOsdBootstrapFailureMessage => {
  if (typeof data !== 'object' || data === null) return false;

  return (data as IOsdBootstrapFailureMessage).type === LOGZIO_OSD_BOOTSTRAP_FAILURE_MESSAGE_TYPE;
};

/** Cross-realm iframes fail `instanceof Window` — each realm has its own constructor. */
export const isOsdProductIframeWindow = (source: MessageEventSource | null): source is IOsdProductIframeWindow => {
  if (source === null || typeof source !== 'object') return false;

  return 'location' in source;
};

export const isOsdBootstrapFailureMessage = (event: MessageEvent<unknown>): event is OsdBootstrapFailureMessageEvent => {
  return event.origin === window.location.origin && isOsdBootstrapFailurePayload(event.data);
};

/** After a kibana iframe load, clear retry state only when no retry is in flight. */
export const scheduleOsdBootstrapRecoverySuccess = (): void => {
  cancelOsdBootstrapRecoverySuccessSchedule();

  successCheckTimeout = setTimeout(() => {
    // iframe onLoad fires before bootstrap finishes; never clear while a retry is active.
    if (readRetries() === 0) {
      clearRetries();
    }

    successCheckTimeout = null;
  }, SUCCESS_WINDOW_MS);
};

export const listenForOsdBootstrapFailure = (
  isTargetIframe: (source: IOsdProductIframeWindow) => boolean,
): (() => void) => {
  const handler = (event: MessageEvent<unknown>) => {
    if (!isOsdBootstrapFailureMessage(event)) return;

    const { source } = event;

    if (!isOsdProductIframeWindow(source)) return;

    if (!isTargetIframe(source)) return;

    cancelOsdBootstrapRecoverySuccessSchedule();

    const { failedUrl } = event.data;
    const message = failedUrl
      ? `OSD bootstrap failure reported by iframe: ${failedUrl}`
      : 'OSD bootstrap failure reported by iframe';

    LoggerService.logError({
      message,
      error: new Error('osd-bootstrap-failure'),
      uxType: UxType.NONE,
    });

    tryRecoverFromOsdBootstrapFailure(source);
  };

  window.addEventListener('message', handler);

  return () => window.removeEventListener('message', handler);
};
